import { BROWSER_ERROR_KEYS, instrumentBrowserError } from '@library/observability/browser'
import React, { useEffect, useMemo } from 'react'
import type { z } from 'zod'

import { Carousel, CarouselFieldsSchema } from '../blocks/Carousel'
import type { CarouselFields } from '../blocks/Carousel'
import { FAQs, FaqsFieldsSchema } from '../blocks/FAQs'
import type { FaqsFields } from '../blocks/FAQs'
import { Hero, HeroFieldsSchema } from '../blocks/Hero'
import type { HeroFields } from '../blocks/Hero'
import { ImageTextColumns, ImageTextColumnsFieldsSchema } from '../blocks/ImageTextColumns'
import type { ImageTextColumnsFields } from '../blocks/ImageTextColumns'
import { StepsWithImage, StepsWithImageFieldsSchema } from '../blocks/StepsWithImage'
import type { StepsWithImageProps } from '../blocks/StepsWithImage'
import { Table, TableFieldsSchema } from '../blocks/Table'
import type { TableFields } from '../blocks/Table'
import { Testimonials, TestimonialsFieldsSchema } from '../blocks/Testimonials'
import type { TestimonialsFields } from '../blocks/Testimonials'
import { VideoContentSection, VideoContentSectionFieldsSchema } from '../blocks/VideoContentSection'
import type { VideoContentSectionFields } from '../blocks/VideoContentSection'
import type { OnTriggerPromoCode } from '../cta'
import { SafeBlock } from './SafeBlock'
import type { CmsPageFields, CmsPageSection } from './types'

// Props every block receives from the page, on top of its own validated fields.
type BlockRenderProps = {
  onTriggerPromocode?: OnTriggerPromoCode
}

type BlockDefinition = {
  schema: z.ZodTypeAny
  render: (fields: unknown, props: BlockRenderProps) => JSX.Element
}

// Maps each block's content-type uid to its Zod validator and a render
// function. `render` receives the section's data (already validated by the
// schema) plus the page-level props, and returns the block's component.
//
// `fields` is typed `unknown` because every block has a different shape, so we
// cast it to that block's `Fields` type before passing it in. The cast is safe:
// `fields` is always the output of this block's own `schema.safeParse`. Only
// blocks with a CTA take `onTriggerPromocode`. Unknown uids are skipped.
const BLOCK_REGISTRY: Record<string, BlockDefinition> = {
  carousel: {
    schema: CarouselFieldsSchema,
    render: (fields, props) => <Carousel {...(fields as CarouselFields)} {...props} />,
  },
  faqs: {
    schema: FaqsFieldsSchema,
    render: (fields) => <FAQs {...(fields as FaqsFields)} />,
  },
  hero: {
    schema: HeroFieldsSchema,
    render: (fields, props) => <Hero {...(fields as HeroFields)} {...props} />,
  },
  image_text_columns: {
    schema: ImageTextColumnsFieldsSchema,
    render: (fields, props) => (
      <ImageTextColumns {...(fields as ImageTextColumnsFields)} {...props} />
    ),
  },
  steps_with_image: {
    schema: StepsWithImageFieldsSchema,
    render: (fields, props) => <StepsWithImage {...(fields as StepsWithImageProps)} {...props} />,
  },
  table: {
    schema: TableFieldsSchema,
    render: (fields) => <Table {...(fields as TableFields)} />,
  },
  testimonials: {
    schema: TestimonialsFieldsSchema,
    render: (fields) => <Testimonials {...(fields as TestimonialsFields)} />,
  },
  video_content_section: {
    schema: VideoContentSectionFieldsSchema,
    render: (fields, props) => (
      <VideoContentSection {...(fields as VideoContentSectionFields)} {...props} />
    ),
  },
}

type Props = {
  page: CmsPageFields
  onTriggerPromocode?: OnTriggerPromoCode
}

type ValidatedEntry = {
  render: (props: BlockRenderProps) => JSX.Element
  blockKey: string
}

type ValidationError = {
  blockUid: string
  sectionIndex: number
  issues: Array<{ path: Array<string | number>; message: string; code: string }>
}

export function PageRenderer({ page, onTriggerPromocode }: Props): JSX.Element | null {
  const { entries, errors } = useMemo<{
    entries: Array<ValidatedEntry | null>
    errors: ValidationError[]
  }>(() => {
    if (!page.sections || page.sections.length === 0) return { entries: [], errors: [] }

    const validationErrors: ValidationError[] = []

    const validatedEntries = page.sections.map((section: CmsPageSection, index) => {
      const blockUid = Object.keys(section)[0]
      if (!blockUid) return null

      const block = BLOCK_REGISTRY[blockUid]
      if (!block) return null

      const payload = (section as Record<string, unknown>)[blockUid]
      const result = block.schema.safeParse(payload)
      if (!result.success) {
        validationErrors.push({
          blockUid,
          sectionIndex: index,
          issues: result.error.issues.map((i) => ({
            path: i.path,
            message: i.message,
            code: i.code,
          })),
        })
        return null
      }

      return {
        render: (props: BlockRenderProps) => block.render(result.data, props),
        blockKey: `${blockUid}-${index}`,
      }
    })

    return { entries: validatedEntries, errors: validationErrors }
  }, [page.sections])

  // We only want to emit validation errors once per unique error occurrence,
  // so we fire them from a useEffect that depends on the error array.
  // The error objects are stable (created in the useMemo above) so this won't cause duplicate emissions.
  useEffect(() => {
    errors.forEach((error) => {
      instrumentBrowserError(BROWSER_ERROR_KEYS.CMS_BLOCK_VALIDATION_ERROR, error)
    })
  }, [errors])

  if (entries.length === 0) return null

  return (
    <>
      {entries.map((entry) =>
        entry ? (
          <SafeBlock key={entry.blockKey} blockKey={entry.blockKey}>
            {entry.render({ onTriggerPromocode })}
          </SafeBlock>
        ) : null,
      )}
    </>
  )
}
